import Layout from './components/layouts/Layout'; // ✅ new
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import Login from './pages/Login'
// import Register from './pages/Register' // optional if created
// import Dashboard from './pages/Dashboard' // optional protected page
// import NotFound from './pages/NotFound' // optional 404
import PrivateRoute from './routes/PrivateRoute'
import Dashboard from './pages/Dashboard'
import TransactionsPage from './pages/TransactionsPage'
import ReportsPage from './pages/Reports';
import OnboardingForm from './components/onboarding/OnboardingForm';
import BulkUploadTransactionForm from './components/transactions/BulkUploadTransactionForm';
import ProtectedOnboardingRoute from './routes/ProtectedOnboardingRoute';
import MyCalendarPage from './pages/MyCalendar';
import BudgetPage from './pages/BudgetPage';
import MilestonesPage from './pages/Milestones';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import CreditCardsAccountsPage from './pages/CreditCardsAccountsPage';
import CheckingAccountsPage from './pages/CheckingAccountsPage';
import SavingsAccountsPage from './pages/SavingsAccountsPage';
import AccountsPage from './pages/AccountsPage';
import { ROLES } from './constants/roles';
import { onAuthFailure } from './lib/authFailure';
import { useEffect } from 'react';

const SETTINGS_ALLOWED_ROLES = [ROLES.SUPER_ADMIN, ROLES.PRIMARY, ROLES.PARTNER];
const ADMIN_ALLOWED_ROLES = [ROLES.SUPER_ADMIN];
const getPostLoginPath = (role?: string) =>
  role === ROLES.SUPER_ADMIN ? '/admin' : '/dashboard';

const AuthFailureListener = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthFailure((reason) => {
      logout();
      navigate(`/login?reason=${reason}`, { replace: true });
    });

    return unsubscribe;
  }, [logout, navigate]);

  return null;
};

export const AppRoutes = () => {
  const { auth } = useAuth();

  return (
    <>
      <AuthFailureListener />
      <Routes>

      <Route
        path="/login"
        element={!auth.token ? <Login /> : <Navigate to={getPostLoginPath(auth.user?.role)} replace />}
      />

      <Route path="/onboarding"
        element={
          <PrivateRoute>
            <Layout>
              <OnboardingForm />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
        }
      />


      <Route
        path="/notifications"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <NotificationsPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner', 'child']}>
            <Layout>
              <ProfilePage />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <PrivateRoute allowedRoles={SETTINGS_ALLOWED_ROLES}>
            <Layout>
              <SettingsPage />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <PrivateRoute allowedRoles={ADMIN_ALLOWED_ROLES}>
            <Layout>
              <AdminPage />
            </Layout>
          </PrivateRoute>
        }
      />

      <Route
        path="/transactions"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <TransactionsPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/accounts"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <AccountsPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/accounts/credit-cards"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <CreditCardsAccountsPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/accounts/checking"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <CheckingAccountsPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/accounts/savings"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <SavingsAccountsPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/transactions/bulk-upload"
        element={
          <ProtectedOnboardingRoute>
            <Layout>
              <BulkUploadTransactionForm />
            </Layout>
          </ProtectedOnboardingRoute>
        }
      />

      <Route
        path="/goals"
        element={<Navigate to="/milestones" replace />}
      />

      <Route
        path="/reports"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <ReportsPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/calendar"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <MyCalendarPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />


      <Route
        path="/budgets"
        element={
          <PrivateRoute allowedRoles={['super admin', 'primary', 'partner']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <BudgetPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route
        path="/milestones"
        element={
          <PrivateRoute allowedRoles={['primary', 'partner', 'child']}>
            <ProtectedOnboardingRoute>
              <Layout>
                <MilestonesPage />
              </Layout>
            </ProtectedOnboardingRoute>
          </PrivateRoute>
        }
      />

      <Route path="/" element={<Navigate to="/login" />} />
      </Routes>
    </>
  )
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  )
}

export default App
